import styled from "@emotion/styled";
import { useLocation } from "react-router-dom";
import {
  AppError,
  Button,
  FieldError,
  FlexRow,
  Form,
  Input,
  Label,
  Link,
  RequiredText,
  Text,
} from "atoms";
import { fieldOptions, validators } from "utils/form";
import { useToggle } from "utils/hooks";
import { useEmailSignInForm } from "../api/useEmailSignInForm";

type EmailSignProps = {
  toggleConnectMethod: VoidFunction;
};

function EmailSignIn({ toggleConnectMethod }: EmailSignProps) {
  const [isShowingPwd, togglePwd] = useToggle(false);
  const location = useLocation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    mutation: { mutate, isLoading, error },
  } = useEmailSignInForm({
    defaultValues: { email: location.state?.enteredEmail ?? "" },
  });

  const enteredEmail = watch("email");

  const onSubmit = handleSubmit((fields) => mutate(fields));

  return (
    <SForm onSubmit={onSubmit}>
      <Label>
        Email <RequiredText />
        <Input
          {...register("email", {
            ...fieldOptions.required,
            validate: validators.validEmail,
          })}
          autoComplete="username"
        />
        {errors.email && <FieldError>{errors.email.message}</FieldError>}
      </Label>

      <PwdWrapper>
        <Label>
          Password <RequiredText />
          <Input
            {...register("password", fieldOptions.required)}
            type={isShowingPwd ? "text" : "password"}
            autoComplete="current-password"
            autoCapitalize="none"
          />
          {errors.password && (
            <FieldError>{errors.password.message}</FieldError>
          )}
        </Label>

        <PwdToggleButton variant="blank" size="xxs" onClick={togglePwd}>
          {isShowingPwd ? "Hide" : "Show"}
        </PwdToggleButton>
      </PwdWrapper>

      <FlexRow justifyContent="center">
        <Button type="submit" disabled={isLoading}>
          Sign In
        </Button>
      </FlexRow>

      {error instanceof Error && (
        <AppError className="AppError">
          {error.message.includes("Unable to authorize")
            ? "Incorrect email or password"
            : error.message}
        </AppError>
      )}

      <FlexRow justifyContent="center" gap="10px">
        <Button variant="text" size="xs" onClick={toggleConnectMethod}>
          Sign in with Wallet
        </Button>
        <Text size="xs" as="span">
          |
        </Text>

        <Link to="/forgot-password" size="xs" state={{ enteredEmail }}>
          Forgot password?
        </Link>
      </FlexRow>
    </SForm>
  );
}

const SForm = styled(Form)(({ theme }) => ({
  textAlign: "left",
  marginBottom: theme.spacing.lg,
  ".AppError": {
    margin: 0,
  },
}));

const PwdWrapper = styled.div({
  position: "relative",
});

const PwdToggleButton = styled(Button)(({ theme }) => ({
  opacity: 0.6,
  padding: "3px 10px",
  position: "absolute",
  right: 3,
  top: 0,
  "&:hover,&:focus": {
    opacity: 1,
    borderColor: theme.colors.transparent,
  },
}));

export { EmailSignIn, SForm, PwdToggleButton, PwdWrapper };
